import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics } from '@/mocks/comics';
import { getChaptersByComicId } from '@/mocks/chapters';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find(c => c.id === Number(id));
  const [activeTab, setActiveTab] = useState<'chapters' | 'info'>('chapters');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  const chapters = comic ? getChaptersByComicId(comic.id) : [];

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - ${comic.author} - 韩国漫画网在线免费阅读`;
    } else {
      document.title = '漫画未找到 - 韩国漫画网';
    }
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-book-2-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的漫画不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium cursor-pointer hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter(c => c.id !== comic.id && c.categories.some(cat => comic.categories.includes(cat)))
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 whitespace-nowrap overflow-x-auto" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${comic.categories[0] === '恋爱' ? 'romance' : comic.categories[0] === '奇幻' ? 'fantasy' : comic.categories[0] === '动作' ? 'action' : comic.categories[0] === '悬疑' ? 'mystery' : comic.categories[0] === '剧情' ? 'drama' : 'comedy'}`} className="hover:text-primary-500 transition-colors">
              {comic.categories[0]}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{comic.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 mb-16">
            <div className="lg:w-[300px] flex-shrink-0">
              <div className="rounded-2xl overflow-hidden aspect-[3/4] sticky top-24">
                <img
                  src={comic.cover}
                  alt={`${comic.title} 韩国漫画封面`}
                  title={`${comic.title} - ${comic.author}`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {comic.categories.map(cat => (
                  <span key={cat} className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-label font-medium">
                    {cat}
                  </span>
                ))}
                <span className={`px-3 py-1 rounded-full text-xs font-label font-medium ${
                  comic.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-200 text-secondary-800'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-6">
                作者：{comic.author} · 共 {comic.chapters} 话 · 最后更新 {comic.updateTime}
              </p>

              <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-8">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500 text-lg"></i>
                  <span className="text-lg font-heading font-bold text-foreground-950">{comic.rating}</span>
                  <span className="text-xs text-foreground-500">/ 5.0</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <i className="ri-eye-line"></i>
                  <span>{(comic.views / 10000).toFixed(0)}万 阅读</span>
                </div>
              </div>

              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
                {comic.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-label font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap flex items-center gap-2">
                  <i className="ri-play-circle-line"></i>从第一话开始阅读
                </button>
                <button className="px-8 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm font-label font-medium cursor-pointer hover:bg-foreground-50 transition-colors whitespace-nowrap flex items-center gap-2">
                  <i className="ri-bookmark-line"></i>加入收藏
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-background-200 pt-8 mb-16">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setActiveTab('chapters')}
                  className={`px-5 py-2 rounded-full text-sm font-label cursor-pointer transition-colors whitespace-nowrap ${
                    activeTab === 'chapters' ? 'bg-background-50 text-foreground-950 font-medium' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  章节列表 ({chapters.length})
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-5 py-2 rounded-full text-sm font-label cursor-pointer transition-colors whitespace-nowrap ${
                    activeTab === 'info' ? 'bg-background-50 text-foreground-950 font-medium' : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  作品信息
                </button>
              </div>
              <button
                onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                aria-label={viewMode === 'list' ? '切换为网格视图' : '切换为列表视图'}
              >
                <i className={`text-foreground-600 ${viewMode === 'list' ? 'ri-layout-grid-line' : 'ri-list-check'}`}></i>
              </button>
            </div>

            {activeTab === 'chapters' && (
              viewMode === 'list' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}/chapter/${ch.number}`}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {ch.title}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.updateTime}</span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {chapters.map((ch) => (
                    <Link
                      key={ch.id}
                      to={`/comic/${comic.id}/chapter/${ch.number}`}
                      className="flex flex-col items-center justify-center p-4 rounded-xl hover:bg-background-100 transition-colors cursor-pointer group border border-background-200 hover:border-primary-300"
                    >
                      <span className="text-2xl font-heading font-bold text-foreground-400 group-hover:text-primary-500 transition-colors mb-1">
                        {ch.number}
                      </span>
                      <span className="text-xs text-foreground-500 text-center line-clamp-1">
                        {ch.title}
                      </span>
                    </Link>
                  ))}
                </div>
              )
            )}

            {activeTab === 'info' && (
              <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">作者</p>
                    <p className="text-sm font-label font-medium text-foreground-950">{comic.author}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">状态</p>
                    <p className="text-sm font-label font-medium text-foreground-950">{comic.status}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">总话数</p>
                    <p className="text-sm font-label font-medium text-foreground-950">{comic.chapters} 话</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400 mb-1">最近更新</p>
                    <p className="text-sm font-label font-medium text-foreground-950">{comic.updateTime}</p>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-xs text-foreground-400 mb-2">作品简介</p>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>
              </div>
            )}
          </div>

          {relatedComics.length > 0 && (
            <div className="pb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5" data-product-shop="">
                {relatedComics.map((rc) => (
                  <Link key={rc.id} to={`/comic/${rc.id}`} className="group cursor-pointer">
                    <div className="relative rounded-xl overflow-hidden aspect-[3/4] mb-3">
                      <img
                        src={rc.cover}
                        alt={rc.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-heading font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">{rc.title}</h3>
                    <p className="text-xs text-foreground-500">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ComicSeries",
          "name": comic.title,
          "author": { "@type": "Person", "name": comic.author },
          "description": comic.description,
          "url": `http://szfirstteam.com/comic/${comic.id}`,
          "inLanguage": "zh-CN",
          "numberOfEpisodes": comic.chapters,
          "dateModified": comic.updateTime,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": comic.rating,
            "bestRating": "5",
            "ratingCount": comic.views
          },
          "isPartOf": {
            "@type": "WebSite",
            "name": "韩国漫画网",
            "url": "http://szfirstteam.com/"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://szfirstteam.com/" },
              { "@type": "ListItem", "position": 2, "name": comic.categories[0], "item": `http://szfirstteam.com/category/${comic.categories[0]}` },
              { "@type": "ListItem", "position": 3, "name": comic.title, "item": `http://szfirstteam.com/comic/${comic.id}` }
            ]
          }
        })}
      </script>
    </>
  );
}